// src/services/guestSend.js — turns one outbox job into one sent message.
//
// The outbox knows about queues, retries and percentages and nothing about Allo.
// This file is the other half: it knows which sender each kind of message needs
// and what shape its payload takes. Keeping them apart is what let the outbox be
// unit-tested against a fake IndexedDB without a Firebase project anywhere near
// it — and it means adding a message type later is a case in one switch.
//
// 🟥 EVERY SENDER IS HANDED THE JOB'S OWN ID AS ITS clientTempId. That is the
//    entire retirement mechanism: the optimistic bubble and the Firestore
//    document share one id, so when the real document arrives in the live query
//    the queue drops its copy and the bubble never blinks. A sender that
//    generated its own id instead would leave the placeholder on screen next to
//    the real message — two bubbles for one send.
//
// 🟥 AND THE REPLY IS SEALED HERE, NOT AT ENQUEUE TIME. Sealing needs every
//    participant's device list, which is a network read — do it while queueing
//    and composing a reply with no signal fails outright, which is precisely the
//    case the queue exists to survive. The job instead carries `replySeed`: the
//    handful of plain scalars quoteFrom() actually reads, which is
//    structured-cloneable and therefore survives IndexedDB. The seal happens at
//    send time, when there is by definition a connection.

import { sendTextMessage } from './messages';
import { sendMediaMessage } from './mediaUpload';
import { sendDocumentMessage } from './documents';
import { sendVoiceMessage, sendLocationMessage } from './voiceAndPlace';
import { buildReplyFields } from './replies';
import { prepareFile } from './mediaTools';

export const TEXT = 'text';
export const MEDIA = 'media';
export const DOCUMENT = 'document';
export const VOICE = 'voice';
export const LOCATION = 'location';

/**
 * The clone-safe shadow of a message being replied to.
 *
 * 🟢 A DECRYPTED MESSAGE CANNOT GO IN INDEXEDDB AS-IS. It carries blob: URLs
 *    (dead after a reload), and in the album case an array of objects holding
 *    more of them. These are the fields quoteFrom() and quoteTextFor() read and
 *    nothing else, all of them strings, numbers or null.
 */
export function replySeed(m) {
  if (!m?.id) return null;
  const pick = (v) => (v === undefined ? null : v);
  return {
    id: m.id,
    senderId: pick(m.senderId),
    type: m.type || 'text',
    systemType: pick(m.systemType),
    callData: pick(m.callData),
    imageUrl: pick(m.imageUrl),
    videoUrl: pick(m.videoUrl),
    images: Array.isArray(m.images) ? m.images.filter((x) => typeof x === 'string') : null,
    imageThumbnailBase64: pick(m.imageThumbnailBase64),
    videoThumbnailBase64: pick(m.videoThumbnailBase64),
    batchThumbnails: Array.isArray(m.batchThumbnails) ? m.batchThumbnails.slice(0, 4) : null,
    documentName: pick(m.documentName),
    // the text the quote will show, already decrypted — see quoteTextFor()
    _plain: pick(m._plain),
    _caption: pick(m._caption),
    text: pick(m.text),
    _payload: (m._payload && typeof m._payload === 'object' && !Array.isArray(m._payload))
      ? { question: pick(m._payload.question), name: pick(m._payload.name) } : null,
  };
}

/** What the composer should show while this job is on screen. */
export function jobPreviewText(job) {
  switch (job?.kind) {
    case TEXT: return job.payload?.text || '';
    case DOCUMENT: return job.payload?.name || 'Document';
    case VOICE: return 'Voice message';
    case LOCATION: return job.payload?.name || 'Location';
    case MEDIA: {
      const n = job.payload?.files?.length || 0;
      return n > 1 ? `${n} items` : 'Photo';
    }
    default: return 'Message';
  }
}

/**
 * Run one job.
 *
 * @param job      the outbox row
 * @param ctx      { chat, myUid, myName, participants }
 * @param hooks    { onProgress, registerAbort } from the outbox runner
 */
export async function performJob(job, ctx, hooks = {}) {
  const { chat, myUid, myName } = ctx;
  const participants = ctx.participants || chat?.participants || [];
  const { onProgress, registerAbort } = hooks;
  const p = job.payload || {};

  const reply = p.replySeed
    ? await buildReplyFields(
      // buildReplyFields reads `.id` via quoteFrom; the seed carries it verbatim
      p.replySeed, participants,
    ).catch(() => ({}))
    : {};

  switch (job.kind) {
    case TEXT:
      /* 🟢 TEXT HAS NO UPLOAD, SO ITS PROGRESS IS A STEP, NOT A RAMP. Reporting a
         fake percentage would be an animation pretending to measure something.
         The bubble shows a "sending" clock instead — see the renderer. */
      await sendTextMessage({
        chatId: chat.id,
        participants,
        senderId: myUid,
        text: p.text,
        isGroup: false,
        reply,
        linkMetadata: p.linkMetadata || undefined,
        tempId: job.id,
      });
      onProgress?.(1);
      return;

    case MEDIA: {
      const files = p.files || [];
      if (!files.length) throw new Error('nothing to send');
      /* 🟥 prepareFile RUNS HERE, NOT AT ENQUEUE. It decodes, re-encodes and
         thumbnails the image — hundreds of milliseconds of main-thread work per
         file, and its output is a Uint8Array several megabytes wide that would
         otherwise be written into IndexedDB alongside the original. The File is
         the small, durable thing to keep; the prepared bytes are derived and
         cheap to make again on a retry. */
      const items = [];
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        items.push(await prepareFile(f));
      }
      await sendMediaMessage({
        chat, myUid, myName, items,
        caption: p.caption || '',
        tempId: job.id,
        reply,
        onProgress,
        registerAbort,
      });
      return;
    }

    case DOCUMENT:
      await sendDocumentMessage({
        chat, myUid, myName,
        file: p.file,
        tempId: job.id,
        reply,
        onProgress,
        registerAbort,
      });
      return;

    case VOICE:
      await sendVoiceMessage({
        chat, myUid, myName,
        blob: p.blob,
        duration: p.secs,
        waveform: p.waveform || [],
        tempId: job.id,
        reply,
        onProgress,
        registerAbort,
      });
      return;

    case LOCATION:
      await sendLocationMessage({
        chat, myUid, myName,
        lat: p.lat, lng: p.lng,
        name: p.name || undefined,
        tempId: job.id,
        reply,
      });
      onProgress?.(1);
      return;

    default:
      throw new Error(`unknown job kind: ${job.kind}`);
  }
}

/**
 * The optimistic message a queued job should be drawn as.
 *
 * 🟢 IT IS SHAPED LIKE A REAL MESSAGE, so the same Bubble renders both and there
 *    is no second, drifting copy of every message type's layout. The underscore
 *    fields are exactly the ones decryptMessage() produces, because that is what
 *    the bubble reads.
 */
export function jobAsMessage(job, myUid, local = {}) {
  const p = job.payload || {};
  /* 🟥 THE PREVIEW URLS ARE MADE BY THE COMPONENT, NOT STORED IN THE JOB. A
     blob: URL is only valid for the document that created it — persist one and
     after a reload the bubble points at a URL the browser has never heard of and
     draws a broken image. The durable thing is the File; the URL is derived from
     it on mount and revoked when the job goes. */
  const urls = local.urls || [];
  const base = {
    id: job.id,
    clientTempId: job.id,
    senderId: myUid,
    timestamp: job.createdAt,
    _outbox: job,                       // the renderer's handle on status/pct
    _pending: job.status !== 'failed',
    _failed: job.status === 'failed',
    replyTo: p.replySeed ? { messageId: p.replySeed.id, senderId: p.replySeed.senderId } : undefined,
  };
  switch (job.kind) {
    case TEXT:
      /* the card shows on the optimistic bubble too — a preview that only appears
         once the round-trip finishes reads as the message changing after it was
         sent */
      return {
        ...base, type: 'text', _plain: p.text, text: p.text,
        ...(p.linkMetadata ? { linkMetadata: p.linkMetadata } : {}),
      };
    case LOCATION:
      return { ...base, type: 'location', _payload: { lat: p.lat, lng: p.lng, name: p.name } };
    case VOICE:
      return {
        ...base, type: 'audio', duration: p.secs, waveform: p.waveform || [],
        _media: local.urls?.[0] || null,
      };
    case DOCUMENT:
      return {
        ...base, type: 'document', documentName: p.name, documentSize: p.size,
        _docThumb: local.thumb || null,
      };
    case MEDIA: {
      const files = p.files || [];
      if (files.length > 1) {
        return {
          ...base, type: 'images', _caption: p.caption || '',
          _mediaBatch: files.map((f, i) => ({
            kind: /^video\//.test(f.type) ? 'video' : 'image', url: urls[i] || null,
          })),
          images: files.map(() => ''),
        };
      }
      const one = files[0];
      return {
        ...base,
        type: one && /^video\//.test(one.type) ? 'video' : 'image',
        _caption: p.caption || '',
        _media: urls[0] || null,
      };
    }
    default:
      return { ...base, type: 'text', _plain: '' };
  }
}
